import React, { useState, useEffect, useCallback } from "react";
import Box from "@mui/material/Box";
import { useDropzone } from "react-dropzone";
import { FileInputProps } from "./types";
import { getDropzoneContainerStyles } from "./styles";
import {
  DEFAULT_FILE_TYPE_ERROR,
  FILE_TOO_LARGE_ERROR,
  MULTIPLE_FILES_ERROR,
} from "./constants";
import ErrorMessage from "./ErrorMessage";
import DefaultContent from "./DefaultContent";
import { formatAcceptedExtensions, validateFileExtension } from "./utils";

const formatMaxSize = (maxSizeInBytes: number): string => {
  const maxSizeInMb = maxSizeInBytes / (1024 * 1024);
  return Number.isInteger(maxSizeInMb)
    ? `${maxSizeInMb}MB`
    : `${maxSizeInMb.toFixed(1)}MB`;
};

const getDropzoneRejectionMessage = (
  rejectedFiles: File[],
  acceptedFileExtensions: string[],
  totalFilesCount: number,
  multiple: boolean,
  maxSize?: number,
  maxFiles?: number
): string | undefined => {
  if (!multiple && totalFilesCount > 1) {
    return MULTIPLE_FILES_ERROR;
  }

  if (maxFiles && totalFilesCount > maxFiles) {
    return `Maximum of ${maxFiles} files allowed`;
  }

  const hasOversizedFile = maxSize
    ? rejectedFiles.some((file) => file.size > maxSize)
    : false;

  if (hasOversizedFile) {
    return maxSize
      ? `${FILE_TOO_LARGE_ERROR}. Maximum size is ${formatMaxSize(maxSize)}.`
      : `${FILE_TOO_LARGE_ERROR}.`;
  }

  const hasInvalidType = rejectedFiles.some(
    (file) => !validateFileExtension(file, acceptedFileExtensions)
  );

  if (hasInvalidType) {
    return DEFAULT_FILE_TYPE_ERROR;
  }

  return rejectedFiles.length > 0 ? DEFAULT_FILE_TYPE_ERROR : undefined;
};

/**
 * A reusable file input component that allows users to drag and drop files or click to select them.
 *
 * @example
 * ```tsx
 * <FileInput
 *   id="csv-upload"
 *   acceptedFileTypes={['text/csv']}
 *   acceptedFileExtensions={['.csv']}
 *   onFilesAccepted={(files) => console.log(files)}
 * />
 * ```
 */
export const FileInput: React.FC<FileInputProps> = ({
  id,
  acceptedFileTypes,
  acceptedFileExtensions,
  multiple = false,
  error: externalError,
  customContent,
  onFilesAccepted,
  maxSize,
  maxFiles,
}) => {
  const [error, setError] = useState<string>(externalError || "");

  // Update error state when external error changes
  useEffect(() => {
    if (!externalError) return;

    setError(externalError);
  }, [externalError]);

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: File[]) => {
      setError("");

      const totalFilesCount = acceptedFiles.length + rejectedFiles.length;
      const rejectionMessage = getDropzoneRejectionMessage(
        rejectedFiles,
        acceptedFileExtensions,
        totalFilesCount,
        multiple,
        maxSize,
        maxFiles
      );

      if (!multiple && totalFilesCount > 1) {
        setError(MULTIPLE_FILES_ERROR);
        return;
      }

      if (rejectionMessage && acceptedFiles.length === 0) {
        setError(rejectionMessage);
        return;
      }

      const validFiles = acceptedFiles.filter((file) =>
        validateFileExtension(file, acceptedFileExtensions)
      );

      if (validFiles.length === 0) {
        setError(rejectionMessage || DEFAULT_FILE_TYPE_ERROR);
        return;
      }

      if (maxFiles && validFiles.length > maxFiles) {
        setError(`Maximum of ${maxFiles} files allowed`);
        return;
      }

      onFilesAccepted(validFiles);
    },
    [multiple, acceptedFileExtensions, onFilesAccepted, maxSize, maxFiles]
  );

  // Include both MIME types and extensions to avoid browser-specific MIME quirks.
  const acceptString = [
    ...new Set([...acceptedFileTypes, ...acceptedFileExtensions]),
  ].join(",");

  // Initialize react-dropzone
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isFocused,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop,
    accept: acceptString,
    multiple,
    maxSize,
  });

  const formattedExtensions = formatAcceptedExtensions(acceptedFileExtensions);
  const isError = !!error || isDragReject;
  const isActive = isDragActive || isFocused || isDragAccept;

  return (
    <Box width="100%">
      <Box
        {...getRootProps()}
        sx={getDropzoneContainerStyles({ isError, isDragActive: isActive })}
        id={id}
        role="button"
        aria-label="File upload zone"
      >
        <input {...getInputProps()} id={`${id}-input`} />

        {customContent || (
          <DefaultContent
            formattedExtensions={formattedExtensions}
            maxSize={maxSize}
          />
        )}

        {isError && <ErrorMessage error={error || DEFAULT_FILE_TYPE_ERROR} />}
      </Box>
    </Box>
  );
};
